import React, { SyntheticEvent, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import Modal from '../../../components/shared/Modal/Modal';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Button from '../../../components/shared/Button/Button';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import Select from '../../../components/shared/Select/Select';
import SearchableSelect from '../../../components/shared/SearchableSelect/SearchableSelect';
import { createExpense, deleteExpense, updateExpense } from '../../../services/expenses';
import type { Expense, ExpenseCategory, Trip } from '../../../types/api';

const tripLabel = (t: Trip): string => {
  const route =
    t.route_name ||
    [t.origin_name, t.destination_name].filter(Boolean).join(' → ') ||
    `Route #${t.route}`;
  const when = t.departure_time
    ? new Date(t.departure_time).toLocaleString([], {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '';
  return `#${t.id} · ${route}${when ? ` · ${when}` : ''}`;
};

const CATEGORY_LABEL: Record<ExpenseCategory, string> = {
  fuel: 'Fuel',
  loading_zone: 'Loading zone',
  driver_pay: 'Driver pay',
  maintenance: 'Maintenance',
  other: 'Other',
};

interface FormState {
  trip: string; category: ExpenseCategory;
  amount: string; description: string;
}
const empty: FormState = { trip: '', category: 'fuel', amount: '', description: '' };

const Expenses: React.FC = () => {
  const { data: expenses, loading, refetch } = usePaginatedResource<Expense>('/finance/expenses/');
  const { data: trips, loading: tripsLoading } = usePaginatedResource<Trip>('/trips/');
  const [editing, setEditing] = useState<Expense | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(empty);
  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null);

  const save = useResourceMutation(
    async (state: FormState) => {
      const payload = {
        trip: parseInt(state.trip, 10),
        category: state.category,
        amount: state.amount,
        description: state.description,
      };
      return editing ? updateExpense(editing.id, payload) : createExpense(payload);
    },
    { successMessage: 'Expense saved' }
  );
  const del = useResourceMutation(
    (id: number) => deleteExpense(id),
    { successMessage: 'Expense deleted' }
  );

  // Searchable trip options. When editing an expense whose trip is no longer in
  // the loaded list, keep a fallback option so the current value stays visible.
  const tripOptions = useMemo(() => {
    const opts = trips.map((t) => ({ value: String(t.id), label: tripLabel(t) }));
    if (
      form.trip &&
      !opts.some((o) => o.value === form.trip)
    ) {
      opts.unshift({
        value: form.trip,
        label: editing?.trip_route
          ? `#${form.trip} · ${editing.trip_route}`
          : `Trip #${form.trip}`,
      });
    }
    return opts;
  }, [trips, form.trip, editing]);

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (e: Expense) => {
    setEditing(e);
    setForm({
      trip: String(e.trip),
      category: e.category,
      amount: e.amount,
      description: e.description,
    });
    setShowForm(true);
  };
  const handleSave = async (ev: SyntheticEvent) => {
    ev.preventDefault();
    try { await save.run(form); setShowForm(false); refetch(); }
    catch { /* toast */ }
  };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try { await del.run(deleteTarget.id); setDeleteTarget(null); refetch(); }
    catch { /* toast */ }
  };

  const columns: Column<Expense>[] = [
    { key: 'trip', header: 'Trip',
      render: (e) => e.trip_route ?? `#${e.trip}` },
    { key: 'category', header: 'Category', filterable: true,
      filterOptions: Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label })),
      render: (e) => CATEGORY_LABEL[e.category] ?? e.category },
    { key: 'amount', header: 'Amount', align: 'right',
      render: (e) => `KES ${Number(e.amount).toLocaleString()}` },
    { key: 'description', header: 'Description' },
    { key: 'created_at', header: 'Date', sortable: true,
      render: (e) => new Date(e.created_at).toLocaleDateString() },
  ];

  const actions = (e: Expense) => (
    <div className="inline-flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={() => openEdit(e)}>Edit</Button>
      <Button variant="danger" size="sm" onClick={() => setDeleteTarget(e)}>Delete</Button>
    </div>
  );

  return (
    <>
      <PageHeader
        title="Expenses"
        description="Trip-level expenses (fuel, driver pay, loading zones, maintenance)."
        actions={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            Record expense
          </Button>
        }
      />
      <DataTable<Expense>
        columns={columns} data={expenses} rowKey="id"
        loading={loading}
        emptyTitle="No expenses recorded"
        emptyDescription="Record fuel, driver pay, and other trip-related expenses."
        emptyAction={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            Record expense
          </Button>
        }
        actions={actions}
      />

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'Edit expense' : 'Record expense'}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={save.loading}>Cancel</Button>
            <Button onClick={handleSave} loading={save.loading} disabled={!form.trip}>{editing ? 'Save changes' : 'Record expense'}</Button>
          </>
        }
      >
        <form onSubmit={handleSave} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Trip" name="trip" required error={save.fieldErrors.trip}>
            <SearchableSelect
              id="trip"
              value={form.trip}
              onChange={(v) => setForm({ ...form, trip: v })}
              options={tripOptions}
              loading={tripsLoading}
              placeholder="Select a trip…"
              searchPlaceholder="Search by route, ID, or date…"
              aria-label="Trip"
            />
          </FormField>
          <FormField label="Category" name="category" required error={save.fieldErrors.category}>
            <Select
              value={form.category}
              onChange={(v) => setForm({ ...form, category: v as ExpenseCategory })}
              options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))}
              fullWidth
            />
          </FormField>
          <FormField label="Amount (KES)" name="amount" required error={save.fieldErrors.amount}>
            <Input id="amount" type="number" step="0.01" required value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </FormField>
          <FormField label="Description" name="description" required error={save.fieldErrors.description}>
            <Input id="description" required value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormField>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete expense?"
        message={`Remove this expense of KES ${deleteTarget?.amount ?? ''}? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={del.loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default Expenses;
